import { makeAutoObservable } from 'mobx';
import { ApiGetSingleCampaignType } from 'src/domains/casino/models/models';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { CampaignStatusType, PromoCodeViewType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { DateTime } from 'src_common/utils/time/time';
import {
    apiDateToDateTime,
    getFieldsFromUsageConditions,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { Amount } from 'src_common/common/amount/Amount';
import { EligibleUsersType } from 'src/domains/casino/webview/bonus-engine/campaign/player-management/eligible-users/EligibleUsers.state';

export class CampaignModel {
    public readonly statusForm: FormInputState<CampaignStatusType, string>; // TOOO: please remove this state from CampaignModel. Model should represent data only

    public constructor(public data: ApiGetSingleCampaignType) {
        makeAutoObservable(this);
        this.statusForm = FormInputState.new(this.data.status);
    }

    public get id(): string {
        return this.data.campaignId;
    }

    public get campaignName(): string {
        return this.data.campaignName ?? '';
    }

    public get description(): string {
        return this.data.description ?? '';
    }

    public get optInOnly(): ApiGetSingleCampaignType['optInOnly'] {
        return this.data.optInOnly;
    }

    public get campaignType(): string {
        return this.data.campaignType ?? '';
    }

    public get campaignTypeForView(): string {
        const type = this.data.campaignType ?? '';

        return type
            .split('-')
            .map((word) => {
                if (word === 'and') {
                    return word;
                }

                return word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join(' ');
    }

    public get startTime(): DateTime | null {
        return apiDateToDateTime(this.data.startAt);
    }

    public get endTime(): DateTime | null {
        return apiDateToDateTime(this.data.expiryAt);
    }

    public get promoCodesArray(): PromoCodeViewType[] | null {
        return this.data.promoCodes ?? null;
    }
    public get startTimeApi(): ApiGetSingleCampaignType['startAt'] {
        return this.data.startAt;
    }

    public get endTimeApi(): ApiGetSingleCampaignType['expiryAt'] {
        return this.data.expiryAt;
    }

    public get bonusRewards(): ApiGetSingleCampaignType['bonusRewards'] {
        return this.data.bonusRewards;
    }

    public get frequencyPeriod(): ApiGetSingleCampaignType['frequencyPeriod'] {
        return this.data.frequencyPeriod;
    }

    public get numberOfUses(): ApiGetSingleCampaignType['numberOfUses'] {
        return this.data.numberOfUses;
    }

    public get actionConditions(): ApiGetSingleCampaignType['actionConditions'] {
        return this.data.actionConditions;
    }

    public get playerType(): ApiGetSingleCampaignType['playerType'] {
        return this.data.playerType;
    }

    public get bonusRewardName(): string | Array<string> {
        return Array.isArray(this.bonusRewards) && this.bonusRewards.length > 0
            ? this.bonusRewards.map((reward) => reward.rewardName ?? '')
            : '';
    }
    public get bonusRewardType(): 'free-bet' | 'free-spin' | null {
        if (!Array.isArray(this.bonusRewards) || this.bonusRewards.length === 0) {
            return null;
        }

        const rewardType = this.bonusRewards[0]?.rewardType ?? null;

        if (rewardType === 'free-spin') {
            return 'free-spin';
        } else if (rewardType === 'free-bet') {
            return 'free-bet';
        }

        return null;
    }

    public get bonusRewardTypeForView(): string | Array<string> {
        return Array.isArray(this.bonusRewards) && this.bonusRewards.length > 0
            ? this.bonusRewards.map((reward) => reward.rewardType?.replace('-', ' ') ?? '')
            : '';
    }

    public get bonusRewardBetType(): string {
        const rewardConditions = getFieldsFromUsageConditions(this);
        return JSON.parse(rewardConditions?.['bet-type'] ?? '')?.join(', ') ?? '';
    }

    public get bonusRewardMinOdds(): string {
        const rewardConditions = getFieldsFromUsageConditions(this);
        return new Amount(rewardConditions?.['bet-min-odds'] ?? '').format('GBP', { withoutCurrencySymbol: true });
    }

    public get totalBet(): Array<number | null | undefined> {
        return Array.isArray(this.bonusRewards) && this.bonusRewards.length > 0
            ? this.bonusRewards.map((reward) => reward.totalBet)
            : [];
    }

    public get playthrough(): Array<string | null | undefined> | null {
        if (!Array.isArray(this.bonusRewards) || this.bonusRewards.length === 0) {
            return null;
        }

        return this.bonusRewards.map((reward) =>
            reward.playthrough !== null && reward.playthrough !== undefined
                ? new Amount(reward.playthrough.toString()).format('GBP', { withoutCurrencySymbol: true })
                : null
        );
    }

    public get rewardAmount(): Array<Amount | null> {
        return Array.isArray(this.bonusRewards) && this.bonusRewards.length > 0
            ? this.bonusRewards.map((reward) =>
                  reward.rewardAmount === null || reward.rewardAmount === undefined
                      ? null
                      : new Amount(reward.rewardAmount.toString())
              )
            : [];
    }

    public get bonusRewardExpiryDate(): DateTime | null {
        return apiDateToDateTime(this.data.bonusRewards?.[0]?.expiryAt);
    }

    public get bonusRewardRelativeExpiryDays(): number | null {
        return this.data.bonusRewards?.[0]?.relativeExpiryDays ?? null;
    }

    public get expiryTypeForView(): { expiryType: string; labelInfo: string } | null | undefined {
        const expiryAt = this.data.bonusRewards?.[0]?.expiryAt;
        return expiryAt === null || expiryAt === undefined
            ? { expiryType: 'Relative expiry', labelInfo: 'Number of days' }
            : { expiryType: 'Absolute expiry', labelInfo: 'End Date & Time (UK Time)' };
    }

    public get bonusRewardExpiryDateApi(): string | null | undefined {
        return this.data.bonusRewards?.[0]?.expiryAt;
    }

    public get installmentCount(): number | null | undefined {
        return this.data.bonusRewards?.[0]?.installmentCount;
    }

    public get rewardInterval(): number | null | undefined {
        return this.data.bonusRewards?.[0]?.rewardInterval;
    }

    public get status(): ApiGetSingleCampaignType['status'] {
        return this.statusForm.value;
    }

    public get promoCodes(): Array<PromoCodeViewType> {
        return this.data.promoCodes ?? [];
    }

    public get eligibleUsers(): EligibleUsersType | null {
        return this.data.eligibleUsers ?? null;
    }

    public get timeLimitTrigger(): ApiGetSingleCampaignType['timeLimitTrigger'] {
        return this.data.timeLimitTrigger ?? null;
    }

    public get timeLimit(): ApiGetSingleCampaignType['timeLimit'] {
        return this.data.timeLimit ?? null;
    }
}
